import React from "react";
import Select, { components } from "react-select";
import { RequiredSign } from "../../Constant/common";

/* Same CustomInput fix as BaseSelect — prevents global CSS from styling the search input */
const CustomInput = (props) => <components.Input {...props} data-rs-input="true" />;

/* Shared style builder — identical to BaseSelect but with multi-value support */
const buildStyles = (isInvalid) => ({
  control: (_, state) => ({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    backgroundColor: "var(--input-bg)",
    border: `1px solid ${
      isInvalid
        ? "var(--danger)"
        : state.isFocused
          ? "var(--input-border-focus)"
          : "var(--input-border)"
    }`,
    borderRadius: 12,
    minHeight: 40,
    boxShadow: state.isFocused
      ? isInvalid
        ? "0 0 0 3px rgba(244,63,94,0.1)"
        : "0 0 0 3px rgba(99,102,241,0.12)"
      : "none",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    pointerEvents: "auto",
    transition: "border-color 0.2s, box-shadow 0.2s",
  }),
  valueContainer: () => ({
    display: "flex",
    alignItems: "center",
    flex: 1,
    flexWrap: "wrap",
    padding: "0 10px",
    backgroundColor: "transparent",
    gap: 4,
  }),
  input: () => ({
    backgroundColor: "transparent",
    color: "var(--text-primary)",
    margin: 0,
    padding: 0,
    fontSize: "0.8125rem",
    fontFamily: "Inter, sans-serif",
    minWidth: 2,
    border: 0,
    outline: 0,
  }),
  placeholder: () => ({
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  indicatorsContainer: () => ({
    display: "flex",
    alignItems: "center",
    backgroundColor: "transparent",
    flexShrink: 0,
    alignSelf: "stretch",
  }),
  indicatorSeparator: () => ({
    width: 1,
    backgroundColor: "var(--input-border)",
    alignSelf: "stretch",
    margin: "8px 0",
  }),
  dropdownIndicator: (_, state) => ({
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    color: "var(--text-muted)",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.2s",
  }),
  clearIndicator: () => ({
    display: "flex",
    alignItems: "center",
    padding: "0 4px",
    color: "var(--text-muted)",
    cursor: "pointer",
  }),
  menu: () => ({
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: "var(--dropdown-bg)",
    border: "1px solid var(--dropdown-border)",
    borderRadius: 12,
    boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
    zIndex: 9999,
    overflow: "hidden",
  }),
  menuList: () => ({
    padding: 4,
    maxHeight: 260,
    overflowY: "auto",
  }),
  option: (_, state) => ({
    backgroundColor: state.isSelected
      ? "rgba(99,102,241,0.15)"
      : state.isFocused
        ? "var(--select-option-hover)"
        : "transparent",
    color: state.isSelected ? "var(--brand-indigo)" : "var(--text-primary)",
    fontSize: "0.8125rem",
    fontWeight: state.isSelected ? 600 : 400,
    padding: "9px 12px",
    borderRadius: 8,
    cursor: "pointer",
  }),
  noOptionsMessage: () => ({
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
    padding: "10px 12px",
    textAlign: "center",
  }),
  multiValue: () => ({
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(99,102,241,0.12)",
    borderRadius: 6,
    margin: 0,
  }),
  multiValueLabel: () => ({
    color: "var(--brand-indigo)",
    fontSize: "0.75rem",
    fontWeight: 600,
    padding: "2px 6px",
  }),
  multiValueRemove: () => ({
    display: "flex",
    alignItems: "center",
    padding: "0 4px",
    color: "var(--brand-indigo)",
    cursor: "pointer",
    borderRadius: "0 6px 6px 0",
    ":hover": {
      backgroundColor: "rgba(99,102,241,0.2)",
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

const MultiSelect = ({
  id,
  value,
  onChange,
  options,
  touched,
  error,
  name,
  placeholder,
  label,
  handleBlur,
  isDisabled,
  required,
}) => {
  const isInvalid = !!(touched && error);

  return (
    <div className="mb-3">
      {label && (
        <label htmlFor={id || name} className="form-label">
          {label}
          {required && <span className="text-danger ms-1">{RequiredSign}</span>}
        </label>
      )}
      <Select
        inputId={id || name}
        name={name}
        isMulti
        unstyled
        styles={buildStyles(isInvalid)}
        components={{ Input: CustomInput }}
        options={options}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={handleBlur}
        isDisabled={isDisabled}
        menuPortalTarget={document.body}
      />
      {isInvalid && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

export default MultiSelect;
